import {
  type CodeInterpreterBlock,
  type CodeInterpreterMessageContent,
  type CodeInterpreterTextBlock,
  type CodeInterpreterThinkingBlock,
  type CodeInterpreterToolCallBlock,
  type MessageContent
} from "@nexus/types"
import { DbEnum } from "@nexus/types/core"
import { hasRenderableContent, isExecutionEventContent } from "@nexus/types/domain"
import type { PayloadAction } from "@reduxjs/toolkit"

import type { OptimisticMessage } from "@/common/types/chat"

import type { EmbedChatState } from "../embedTypes"
import { isHtmlTemplateCardContent } from "./messages"

/**
 * The wire types `toolType` as a bare string (`MessageCompletePayload`), while the
 * store holds `DbEnum.ToolType`. Narrow by value rather than by declaration: a
 * string outside the enum is dropped instead of being stored as a tool type.
 */
const toToolType = (value: string | undefined): DbEnum.ToolType | undefined =>
  Object.values(DbEnum.ToolType).find((toolType) => toolType === value)

/**
 * Add streaming token to message
 */
export const addEmbedStreamingToken = (
  state: EmbedChatState,
  action: PayloadAction<{ chatId: string; messageId: string; token: string }>
) => {
  const { chatId, messageId, token } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  if (chat.streamingMessages[messageId]) {
    chat.streamingMessages[messageId].content += token
  } else {
    chat.streamingMessages[messageId] = {
      content: token,
      isStreaming: true,
      isComplete: false,
      displayComplete: false,
      hasError: false
    }
  }
}

/**
 * Complete streaming message
 * Adds final message to messages array and marks streaming as complete
 * Avoids unmount warning by not deleting (just marking complete)
 */
export const completeEmbedStreamingMessage = (
  state: EmbedChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    /**
     * `MessageCompletePayload.content`, plus the code-interpreter keys a live turn
     * carries on the same object (`codeInterpreterFiles`, `codeInterpreterBlocks`).
     */
    content?: MessageContent | CodeInterpreterMessageContent
    metaConversationId?: string
    contentType?: DbEnum.MessageContentType
    toolType?: string
    toolCalls?: Array<{
      id: string
      type: string
      function: {
        name: string
        arguments: string
      }
    }>
    endsTurn?: boolean
  }>
) => {
  const {
    chatId,
    messageId,
    content: payloadContent,
    metaConversationId,
    contentType: providedContentType,
    toolType: providedToolType,
    toolCalls: providedToolCalls,
    endsTurn
  } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  // Preserve full content object so hasRenderableContent can inspect c.type and c.images
  const finalContent = payloadContent ?? chat.streamingMessages[messageId]?.content
  const streamingContent =
    typeof finalContent === "string" ? finalContent : finalContent?.text || ""

  // Default to AI for backward compatibility with events that omit contentType
  const messageContentType = providedContentType || DbEnum.MessageContentType.AI

  // The agentic turn is over ONLY when an AI message completes with no further
  // tool calls. This mirrors the backend agent loop's own exit condition (it
  // keeps generating while the last message has tool calls or is a TOOL
  // result). A TOOL message, or an AI message that still carries toolCalls,
  // means a skill/RPA is mid-run and another message is coming — so the input
  // must stay disabled to stop the customer re-triggering the skill.
  //
  // The one exception is the backend's own word (NEX-4252): a card that closed
  // its batch under the deployment's `cardEndsTurn` ends the turn with no AI
  // message after it, and its TOOL frame carries `endsTurn` to say so. Without
  // it the composer would stay locked under the card until the page reloaded.
  const hasPendingToolCalls = Array.isArray(providedToolCalls) && providedToolCalls.length > 0
  const agentTurnComplete =
    (messageContentType === DbEnum.MessageContentType.AI && !hasPendingToolCalls) ||
    endsTurn === true

  // Suppress messages that must never appear in the embed chat UI:
  // 1. TOOL messages — raw workflow results (internal plumbing, not user-visible).
  //    Exception: an HTML template card (NEX-2541) is the user-facing payload.
  // 2. Empty AI messages — AI turn that only contains a tool_call with no visible text
  // 3. Execution event envelopes on any role. An event that omits `contentType`
  //    defaults to AI above, so `isTool` never caught one — it reached the
  //    customer as an AI bubble whose text was the raw JSON (NEX-3023).
  const isTool =
    messageContentType === DbEnum.MessageContentType.TOOL &&
    !isHtmlTemplateCardContent(finalContent)
  const isEmpty = !hasRenderableContent(finalContent)
  const isExecutionEvent = isExecutionEventContent(finalContent)
  if (isTool || isEmpty || isExecutionEvent) {
    const streamingData = chat.streamingMessages[messageId]
    if (streamingData) {
      streamingData.isStreaming = false
      streamingData.isComplete = true
      streamingData.displayComplete = true
    }
    // Empty AI message with no tool calls = the agent ended its turn with no
    // visible text. Still re-enable the input so it never gets stuck disabled.
    if (agentTurnComplete) {
      chat.isSendingMessage = false
      chat.shouldShowDynamicWhitespace = false
    }
    return
  }

  const now = new Date()
  const messageToolType = toToolType(providedToolType)
  const messageContent: MessageContent = payloadContent || { text: streamingContent }

  // Add or update in permanent messages array — dedup guards against double-complete events
  const existingMessageIndex = chat.messages.findIndex((m) => m.id === messageId)

  const existingMessage =
    existingMessageIndex !== -1 ? chat.messages[existingMessageIndex] : undefined

  if (existingMessage) {
    // Update existing message (already loaded from history or previous event)
    existingMessage.content = messageContent
    existingMessage.status = DbEnum.MessageStatus.PROCESSED
    existingMessage.updatedAt = now
    if (providedToolCalls) existingMessage.toolCalls = providedToolCalls
    if (providedContentType) existingMessage.type = providedContentType
    if (messageToolType) existingMessage.toolType = messageToolType
    existingMessage.textContent = streamingContent
  } else {
    // Only include fields that are actually used — omitted fields listed below
    const completedMessage: OptimisticMessage = {
      id: messageId,
      chatId,
      content: messageContent,
      type: messageContentType,
      status: DbEnum.MessageStatus.PROCESSED,
      createdAt: now,
      updatedAt: now,
      toolCalls: providedToolCalls || [],
      toolType: messageToolType,
      agentId: chat.deployment?.agent?.id,
      metaConversationId: metaConversationId,
      textContent: streamingContent
      // Removed: fields that are always undefined and unused
      // name, toolCallId, files, images, organizationId, userId,
      // cancelled, statusInfos, timezoneOffsetHours, modelId,
      // integrationMessageId, runId, user, agent
    }
    chat.messages.push(completedMessage)
  }

  // Mark as complete (stops showing in UI) but don't delete (avoids unmount warning)
  if (chat.streamingMessages[messageId]) {
    chat.streamingMessages[messageId].isStreaming = false
    chat.streamingMessages[messageId].isComplete = true
    chat.streamingMessages[messageId].displayComplete = true
  }

  // Re-enable the input only when the agent's turn is truly over (final AI
  // reply, no pending tool calls). If this visible message also carries
  // toolCalls, the agent is about to run a skill/RPA and will send another
  // message — keep the input disabled so the customer can't re-trigger it.
  if (agentTurnComplete) {
    chat.isSendingMessage = false
    chat.shouldShowDynamicWhitespace = false
  }
}

/**
 * Clear streaming message
 */
export const clearEmbedStreamingMessage = (
  state: EmbedChatState,
  action: PayloadAction<{ chatId: string; messageId: string }>
) => {
  const { chatId, messageId } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  delete chat.streamingMessages[messageId]
}

/**
 * Handle streaming error
 * Accepts structured error with code and message from backend
 *
 * This handles two scenarios:
 * 1. Error during active streaming - update existing streamingMessages entry
 * 2. Error before streaming started - create streamingMessages entry AND add failed message to array
 */
export const embedStreamingError = (
  state: EmbedChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    error: { code: string; message: string } | string
  }>
) => {
  const { chatId, messageId, error } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  // Normalize error to object format (support legacy string format)
  const normalizedError =
    typeof error === "string" ? { code: "STREAMING_ERROR", message: error } : error

  // Update or create streaming message entry
  if (chat.streamingMessages[messageId]) {
    chat.streamingMessages[messageId].isStreaming = false
    chat.streamingMessages[messageId].isComplete = true
    chat.streamingMessages[messageId].hasError = true
    chat.streamingMessages[messageId].error = normalizedError
  } else {
    // Error occurred before streaming started - create entry for UI
    chat.streamingMessages[messageId] = {
      content: "",
      isStreaming: false,
      isComplete: true,
      displayComplete: true,
      hasError: true,
      error: normalizedError
    }
  }

  // Also add failed message to messages array if not already present
  // This ensures the error is visible in the chat history
  const existingMessage = chat.messages.find((m) => m.id === messageId)
  if (!existingMessage) {
    const now = new Date()
    const failedMessage: OptimisticMessage = {
      id: messageId,
      chatId,
      content: { text: "" },
      type: DbEnum.MessageContentType.AI,
      status: DbEnum.MessageStatus.FAILED,
      createdAt: now,
      updatedAt: now,
      statusInfos: {
        error: normalizedError
      }
    }
    chat.messages.push(failedMessage)
  } else {
    // Update existing message to FAILED status
    existingMessage.status = "FAILED"
    existingMessage.statusInfos = {
      error: normalizedError
    }
  }

  // Stop sending state
  chat.isSendingMessage = false
  chat.shouldShowDynamicWhitespace = false
}

// =====================================================
// Code Interpreter Block Reducers (unified into streamingMessages)
// =====================================================

/**
 * Adds a new code interpreter block to the streaming message.
 * Blocks are stored in streamingMessages[messageId].blocks for interleaved display.
 */
export const addEmbedStreamingBlock = (
  state: EmbedChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    block: CodeInterpreterBlock
  }>
) => {
  const { chatId, messageId, block } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  // Do NOT clear isSendingMessage here. A code-interpreter block starting
  // means a skill is mid-execution; clearing it would re-enable the input and
  // let the customer re-trigger the skill. It is cleared by
  // completeEmbedStreamingMessage (agentTurnComplete gate) when the agentic
  // turn actually ends.

  // Initialize streaming message if needed
  if (!chat.streamingMessages[messageId]) {
    chat.streamingMessages[messageId] = {
      content: "",
      blocks: [],
      isStreaming: true,
      isComplete: false,
      hasError: false,
      displayComplete: false
    }
  }

  const streamingData = chat.streamingMessages[messageId]
  if (!streamingData.blocks) streamingData.blocks = []

  // Map incoming block to our internal format based on discriminated union type
  if (block.type === "thinking") {
    const thinkingBlock: CodeInterpreterThinkingBlock = {
      type: "thinking",
      id: block.id,
      content: block.content || "",
      isComplete: block.isComplete ?? false
    }
    streamingData.blocks.push(thinkingBlock)
  } else if (block.type === "tool_call") {
    const toolCallBlock: CodeInterpreterToolCallBlock = {
      type: "tool_call",
      id: block.id,
      toolId: block.toolId,
      name: block.name,
      input: block.input,
      isComplete: false
    }
    streamingData.blocks.push(toolCallBlock)
  } else if (block.type === "text") {
    const textBlock: CodeInterpreterTextBlock = {
      type: "text",
      id: block.id,
      content: block.content || ""
    }
    streamingData.blocks.push(textBlock)
  }
}

/**
 * Updates a block's content (for streaming thinking/text updates).
 * If the block doesn't exist yet (due to event ordering), creates it.
 */
export const updateEmbedStreamingBlock = (
  state: EmbedChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    blockId: string
    content: string
  }>
) => {
  const { chatId, messageId, blockId, content } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  // Initialize streaming message if needed
  if (!chat.streamingMessages[messageId]) {
    chat.streamingMessages[messageId] = {
      content: "",
      blocks: [],
      isStreaming: true,
      isComplete: false,
      hasError: false,
      displayComplete: false
    }
  }

  const streamingData = chat.streamingMessages[messageId]
  if (!streamingData.blocks) streamingData.blocks = []

  const block = streamingData.blocks.find((b) => b.id === blockId)

  if (block && (block.type === "thinking" || block.type === "text")) {
    // Block exists - update its content
    block.content = content
  } else if (!block) {
    // Block doesn't exist yet (BLOCK_UPDATE arrived before BLOCK_START)
    // Infer the type from blockId prefix and create it
    const type = blockId.startsWith("thinking-") ? "thinking" : "text"
    if (type === "thinking") {
      streamingData.blocks.push({
        type: "thinking",
        id: blockId,
        content,
        isComplete: false
      })
    } else {
      streamingData.blocks.push({
        type: "text",
        id: blockId,
        content
      })
    }
  }
}

/**
 * Completes a block (typically a tool call with its result).
 */
export const completeEmbedStreamingBlock = (
  state: EmbedChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    blockId: string
    output?: string
    isError?: boolean
  }>
) => {
  const { chatId, messageId, blockId, output, isError } = action.payload
  const chat = state.chats[chatId]

  if (!chat?.streamingMessages[messageId]?.blocks) return

  const block = chat.streamingMessages[messageId].blocks?.find((b) => b.id === blockId)

  if (block && block.type === "tool_call") {
    block.output = output
    block.isError = isError
    block.isComplete = true
  } else if (block && block.type === "thinking") {
    block.isComplete = true
  }
}

/**
 * Marks code interpreter streaming as complete.
 * Keeps all blocks visible until completeEmbedStreamingMessage adds the final message.
 */
export const completeEmbedCodeInterpreterStreaming = (
  state: EmbedChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    content: string
    files?: string[]
  }>
) => {
  const { chatId, messageId, content } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  const streamingData = chat.streamingMessages[messageId]

  if (streamingData) {
    if (content?.trim()) {
      streamingData.content = content
    }
    // Mark streaming as complete but keep all blocks visible
    // The blocks will be replaced when completeEmbedStreamingMessage adds the final message
    streamingData.isStreaming = false
    streamingData.isComplete = true
  }

  // Do NOT clear isSendingMessage here. Code-interpreter execution finishing
  // is followed by the agent's final message; completeEmbedStreamingMessage
  // clears it via the agentTurnComplete gate once the turn is actually over,
  // so the input stays disabled for the whole skill run.
}
